import React, { Fragment, useEffect } from 'react';
import { PictureFilled, PlusOutlined } from '@ant-design/icons';
import { useController } from 'react-hook-form';
import { Button, Upload, Modal, Row, Col } from 'antd';
import { Required, RowComponent } from '../../../styles/global-style';
import { TextXSMall, TextInputLabel } from '../../text';
import { UploadOutlined } from '@ant-design/icons';
import { renderTypeError } from '../index.jsx';
import { useState } from 'react';
import heic2any from 'heic2any';
import { color } from '../../../resources';

/**
 * Convert HEIF/HEIC images to JPEG before processing
 * @param {File} file - The image file to convert if needed
 * @returns {Promise<File>} - A promise that resolves to the converted file (or original if not HEIF)
 */
const convertHeicToJpeg = async (file) => {
  if (
    file.name.toLowerCase().endsWith('.heic') ||
    file.name.toLowerCase().endsWith('.heif') ||
    file.type === 'image/heic' ||
    file.type === 'image/heif'
  ) {
    try {
      const jpegBlob = await heic2any({
        blob: file,
        toType: 'image/jpeg',
        quality: 0.8,
      });

      const originalName = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
      const jpegFile = new File([jpegBlob], `${originalName}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });

      return jpegFile;
    } catch (error) {
      return file;
    }
  }
  return file;
};

/**
 * Resizes an image file to ensure it doesn't exceed the maximum size
 * and converts it to JPG format
 * @param {File} file - The image file to resize
 * @param {number} maxSizeKB - Maximum file size in KB (default: 250KB)
 * @returns {Promise<File>} - A promise that resolves to the resized file in JPG format
 */
const resizeImage = async (file, maxSizeKB = 250) => {
  if (file.size <= maxSizeKB * 1024 && file.type === 'image/jpeg') {
    return file;
  }

  return new Promise((resolve) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target.result;

      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        let quality = 0.9;
        const maxSize = maxSizeKB * 1024;

        const resizeAndCheck = () => {
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          if (file.type !== 'image/jpeg') {
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, width, height);
          }
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob.size <= maxSize) {
                const originalName =
                  file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
                const resizedFile = new File([blob], `${originalName}.jpg`, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                resolve(resizedFile);
              } else {
                if (quality > 0.5) {
                  quality -= 0.1;
                  resizeAndCheck();
                } else {
                  const scaleFactor = Math.sqrt(maxSize / blob.size) * 0.9;
                  width = Math.floor(width * scaleFactor);
                  height = Math.floor(height * scaleFactor);
                  resizeAndCheck();
                }
              }
            },
            'image/jpeg',
            quality,
          );
        };

        resizeAndCheck();
      };
    };

    reader.readAsDataURL(file);
  });
};

const UploadFiles = ({ control, item, setValue, getValues }) => {
  const { rules, name, defaultValue, label, disabled, dataCy, uploadBoxStyle, ...propsInput } =
    item;
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');

  const { fieldState, field } = useController({
    control,
    name,
    rules,
    defaultValue,
  });
  const { error } = fieldState;
  const { value } = field;

  const onChange = async (event) => {
    const fileList = [...event.fileList];

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      if (file.originFileObj && file.originFileObj instanceof File) {
        try {
          if (
            file.originFileObj.type.startsWith('image/') ||
            file.originFileObj.name.toLowerCase().endsWith('.heic') ||
            file.originFileObj.name.toLowerCase().endsWith('.heif')
          ) {
            const convertedFile = await convertHeicToJpeg(file.originFileObj);
            const resizedFile = await resizeImage(convertedFile);
            file.originFileObj = resizedFile;
            file.name = resizedFile.name;
          }
        } catch (error) {
          console.error('Error processing image:', error);
        }
      }
    }

    setValue(name, fileList);
  };

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handleCancel = () => setPreviewOpen(false);
  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
    setPreviewTitle(file.name || file.url.substring(file.url.lastIndexOf('/') + 1));
  };

  const uploadStyle = {
    uploadBox: {
      width: '35vw',
      height: '35vw',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      border: '1px dashed #d9d9d9',
      borderRadius: '8px',
      backgroundColor: '#fafafa',
      textAlign: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s',
      margin: '10px auto',
      padding: '10px',
      '&:hover': {
        borderColor: '#1890ff',
        backgroundColor: '#f0f5ff',
      },
    },
    iconContainer: {
      fontSize: '36px',
      color: '#40a9ff',
      marginBottom: '4px',
    },
    text: {
      color: '#666',
      fontSize: '14px',
    },
    preview: {
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      '& img': {
        maxWidth: '100%',
        maxHeight: '100%',
      },
    },
  };

  useEffect(() => {
    // Cleanup function สำหรับ revoke object URL
    return () => {
      if (value?.length > 0 && value[0].originFileObj) {
        URL.revokeObjectURL(URL.createObjectURL(value[0].originFileObj));
      }
    };
  }, [value]);

  return (
    <Fragment>
      <RowComponent style={{ marginBottom: '30px' }}>
        {label && <TextInputLabel text={label} />}
        {rules && rules.required && <Required>*</Required>}
      </RowComponent>
      <Row gutter={24}>
        <Col span={24}>
          <Upload
            id={name}
            beforeUpload={() => false}
            listType="picture"
            disabled={disabled || false}
            maxCount={1}
            accept="image/*"
            onPreview={handlePreview}
            fileList={getValues(name)}
            onChange={onChange}
            style={uploadBoxStyle ? { width: '100%' } : {}}
            {...propsInput}
          >
            {value?.length >= (item.maxCount || 1) ? null : (
              <div
                style={{ ...uploadStyle.uploadBox, ...uploadBoxStyle }}
                className="hover:border-blue-400 hover:bg-blue-50"
              >
                <div style={uploadStyle.iconContainer}>
                  <PictureFilled style={{ fontSize: '36px', color: color.green_pixel }} />
                </div>
                <div style={uploadStyle.text}>แนบไฟล์</div>
                <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                  เลือกไฟล์จากอุปกรณ์
                </div>
              </div>
            )}
          </Upload>
        </Col>
      </Row>
      {rules && rules.required && <RowComponent style={{ marginTop: '30px' }}></RowComponent>}
      <Modal
        destroyOnClose={true}
        open={previewOpen}
        title={previewTitle}
        footer={null}
        onCancel={handleCancel}
      >
        <img
          alt="example"
          style={{
            width: '100%',
          }}
          src={previewImage}
        />
      </Modal>
      {error && <TextXSMall text={renderTypeError(item, error)} color="red" />}
    </Fragment>
  );
};

export const MemoizedUpload = UploadFiles; //memo(, compareRender);
